/** Bank accounts, credit cards and the transactions imported from their statements. */
import type {
  BankAccountsResponse,
  BookingRulesResponse,
  TransactionsResponse,
} from "@/lib/platform/pb-types";
import { pb } from "@/lib/platform/pocketbase";
import type { ColumnMapping } from "@/lib/import/transactions";
import { accountBalance, statementDifference, unexplained } from "@/lib/banking/money";
import { counterpartyKey, direction } from "@/lib/banking/rules";
import type { DocumentRecord } from "./documents";

export type BankAccount = BankAccountsResponse<ColumnMapping>;
export type Transaction = TransactionsResponse<
  string[],
  string[],
  { document?: DocumentRecord; possibleDuplicateOf?: TransactionsResponse }
>;
export type TransactionStatus = Transaction["status"];

export type AccountInput = {
  label: string;
  type: BankAccount["type"];
  institution: string;
  holder: string;
  iban: string;
  last4: string;
  currency: string;
  /** The Sachkonto the accountant books this card or account on, e.g. 2800. */
  ledgerAccount?: string;
  /** The balance on `openingDate`; rows before it are already in it. */
  openingBalance?: number;
  openingDate?: string;
  /** The balance a statement printed on `statementDate`, checked against the imported rows. */
  statementBalance?: number;
  statementDate?: string;
};

export const ACCOUNT_DEFAULTS: AccountInput = {
  label: "",
  type: "credit_card",
  institution: "",
  holder: "",
  iban: "",
  last4: "",
  currency: "EUR",
};

export async function listAccounts(workspaceId: string): Promise<BankAccount[]> {
  return pb.collection("bank_accounts").getFullList<BankAccount>({
    filter: pb.filter("workspace = {:workspaceId} && archived != true", { workspaceId }),
    sort: "label",
  });
}

export async function createAccount(
  workspaceId: string,
  input: AccountInput,
): Promise<BankAccount> {
  return pb.collection("bank_accounts").create({ ...input, workspace: workspaceId });
}

export async function updateAccount(
  id: string,
  input: Partial<AccountInput>,
): Promise<BankAccount> {
  return pb.collection("bank_accounts").update(id, input);
}

export async function archiveAccount(id: string): Promise<BankAccount> {
  return pb.collection("bank_accounts").update(id, { archived: true });
}

/** Remembers the column mapping of the last import so the next statement needs no setup. */
export async function saveImportPreset(id: string, preset: ColumnMapping): Promise<BankAccount> {
  return pb.collection("bank_accounts").update(id, { importPreset: preset });
}

export type TransactionFilters = {
  account?: string;
  /** "review": rows an import could not tell apart from another one. */
  status?: TransactionStatus | "all" | "review";
  from?: string;
  to?: string;
};

export async function listTransactions(
  workspaceId: string,
  filters: TransactionFilters = {},
): Promise<Transaction[]> {
  const parts = [pb.filter("workspace = {:workspaceId}", { workspaceId })];
  if (filters.account) parts.push(pb.filter("account = {:account}", { account: filters.account }));
  if (filters.status === "review") {
    parts.push('possibleDuplicateOf != ""');
  } else if (filters.status && filters.status !== "all") {
    parts.push(pb.filter("status = {:status}", { status: filters.status }));
  }
  if (filters.from)
    parts.push(pb.filter("bookingDate >= {:from}", { from: `${filters.from} 00:00:00` }));
  if (filters.to) parts.push(pb.filter("bookingDate <= {:to}", { to: `${filters.to} 23:59:59` }));

  return pb.collection("transactions").getFullList<Transaction>({
    filter: parts.join(" && "),
    sort: "-bookingDate",
    expand: "document,possibleDuplicateOf",
  });
}

/** What a row without a receipt is booked as (lib/ledger/money.ts). */
export type BookingType = "category" | "private" | "transfer" | "tax";

export type BookingInput = {
  bookingType: BookingType;
  category?: string;
  transferAccount?: string;
  /** What it was: the Eigenbeleg for a row without a receipt. */
  bookingNote?: string;
};

const UNBOOKED = {
  bookingType: "",
  category: "",
  transferAccount: "",
  transferOf: "",
  bookingNote: "",
};

/**
 * `matchedBy` says who decided: "auto" for the matcher, "person" for everything a person does -
 * which also keeps the matcher away from that row for good.
 */
export async function linkDocument(
  transactionId: string,
  documentId: string,
  matchedBy: "auto" | "person" = "person",
): Promise<Transaction> {
  return pb
    .collection("transactions")
    .update(transactionId, { ...UNBOOKED, document: documentId, status: "matched", matchedBy });
}

/**
 * Undoes a receipt or a booking. A transfer's other half then carries the movement on its own:
 * the posting skips an incoming half only while it is paired.
 */
export async function unlinkDocument(transactionId: string): Promise<Transaction> {
  const paired = await pb.collection("transactions").getFullList<Transaction>({
    filter: pb.filter("transferOf = {:id}", { id: transactionId }),
  });
  for (const other of paired) {
    await pb.collection("transactions").update(other.id, { transferOf: "" });
  }
  return pb
    .collection("transactions")
    .update(transactionId, { ...UNBOOKED, document: "", status: "unmatched", matchedBy: "person" });
}

/**
 * Books a row that has no receipt: to a category, private, the Finanzamt, or as a transfer to
 * another own account. A transfer whose other half was imported too (`transferMirror`) books
 * both, the incoming one paired to the outgoing one, so the movement posts once.
 */
export async function bookWithoutReceipt(
  transaction: Transaction,
  input: BookingInput,
  mirror: Transaction | null = null,
  matchedBy: "auto" | "person" = "person",
): Promise<Transaction> {
  const booking = {
    ...UNBOOKED,
    status: "ignored",
    document: "",
    matchedBy,
    bookingType: input.bookingType,
    category: input.bookingType === "category" ? (input.category ?? "") : "",
    transferAccount: input.bookingType === "transfer" ? (input.transferAccount ?? "") : "",
    bookingNote: input.bookingNote?.trim() ?? "",
  };
  if (input.bookingType !== "transfer" || !mirror) {
    return pb.collection("transactions").update(transaction.id, booking);
  }
  const incoming = transaction.amount > 0;
  await pb.collection("transactions").update(mirror.id, {
    ...booking,
    transferAccount: transaction.account,
    transferOf: incoming ? "" : transaction.id,
  });
  return pb
    .collection("transactions")
    .update(transaction.id, { ...booking, transferOf: incoming ? mirror.id : "" });
}

export type BookingRuleRecord = BookingRulesResponse;

export async function listBookingRules(workspaceId: string): Promise<BookingRuleRecord[]> {
  return pb.collection("booking_rules").getFullList<BookingRuleRecord>({
    filter: pb.filter("workspace = {:workspaceId}", { workspaceId }),
    sort: "counterparty",
  });
}

/**
 * "Book future rows like this": one rule per account, counterparty and direction, so booking the
 * same counterparty differently later replaces the rule instead of adding a second one.
 */
export async function saveBookingRule(
  transaction: Transaction,
  input: BookingInput,
): Promise<BookingRuleRecord> {
  const data = {
    workspace: transaction.workspace,
    account: transaction.account,
    counterparty: counterpartyKey(transaction),
    direction: direction(transaction),
    bookingType: input.bookingType,
    category: input.bookingType === "category" ? (input.category ?? "") : "",
    transferAccount: input.bookingType === "transfer" ? (input.transferAccount ?? "") : "",
    bookingNote: input.bookingNote?.trim() ?? "",
  };
  const existing = await pb.collection("booking_rules").getFullList<BookingRuleRecord>({
    filter: pb.filter(
      "account = {:account} && counterparty = {:counterparty} && direction = {:direction}",
      data,
    ),
  });
  return existing[0]
    ? pb.collection("booking_rules").update<BookingRuleRecord>(existing[0].id, data)
    : pb.collection("booking_rules").create<BookingRuleRecord>(data);
}

export async function deleteBookingRule(id: string): Promise<void> {
  await pb.collection("booking_rules").delete(id);
}

export type ManualEntry = {
  date: string;
  /** Euros; negative is money out. */
  amount: number;
  counterparty: string;
  description?: string;
};

/** A movement typed in by hand, for a till: it has no statement to import. */
export async function createManualTransaction(
  workspaceId: string,
  accountId: string,
  entry: ManualEntry,
): Promise<Transaction> {
  return pb.collection("transactions").create({
    workspace: workspaceId,
    account: accountId,
    bookingDate: entry.date,
    amount: entry.amount,
    currency: "EUR",
    counterparty: entry.counterparty,
    description: entry.description ?? "",
    status: "unmatched",
    // The unique (account, externalId) index needs one; a hand-typed row has no source row.
    externalId: `manual:${crypto.randomUUID()}`,
  });
}

export async function deleteTransaction(id: string): Promise<void> {
  await pb.collection("transactions").delete(id);
}

export type JournalLine = {
  id: string;
  debit: number;
  credit: number;
  expand: {
    entry: {
      id: string;
      seq: number;
      date: string;
      source: string;
      reverses: string;
      docNumber: string;
      partnerName: string;
    };
  };
};

/** Every journal line on one Sachkonto, for the cash book. */
export async function listJournalLines(workspaceId: string, account: string) {
  const lines = await pb.collection("journal_lines").getFullList<JournalLine>({
    filter: pb.filter("workspace = {:workspaceId} && account = {:account}", {
      workspaceId,
      account,
    }),
    expand: "entry",
  });
  return lines.map((line) => ({
    id: line.id,
    debit: line.debit,
    credit: line.credit,
    entry: line.expand.entry,
  }));
}

export type ReceiptGap = {
  account: BankAccount;
  missing: Transaction[];
  missingCount: number;
  missingTotal: number;
  matchedCount: number;
  /** Rows an import flagged as a possible duplicate; not counted as missing until decided. */
  reviewCount: number;
  /** Rows the ledger does not know what to do with yet, money in included. */
  unexplainedCount: number;
  /** Opening balance plus every row, and the difference to the last statement balance entered. */
  balance: number;
  statementDifference: number | null;
};

/** Per account: which charges still have no receipt, and what they add up to. */
export function receiptGaps(accounts: BankAccount[], transactions: Transaction[]): ReceiptGap[] {
  return accounts.map((account) => {
    const own = transactions.filter((transaction) => transaction.account === account.id);
    const missing = own.filter(
      (transaction) =>
        transaction.status === "unmatched" &&
        transaction.amount < 0 &&
        !transaction.possibleDuplicateOf,
    );
    return {
      account,
      missing,
      missingCount: missing.length,
      missingTotal:
        Math.round(
          missing.reduce((sum, transaction) => sum + Math.abs(transaction.amount), 0) * 100,
        ) / 100,
      matchedCount: own.filter((transaction) => transaction.status === "matched").length,
      reviewCount: own.filter((transaction) => Boolean(transaction.possibleDuplicateOf)).length,
      unexplainedCount: unexplained(account, own).length,
      balance: accountBalance(account, transactions),
      statementDifference: statementDifference(account, transactions),
    };
  });
}
